/**
 * دورة حياة المهمة.
 *
 * فصل مقصود بين:
 *   - status        : موقع المهمة في مسار العمل (مجدولة/لم تبدأ/قيد التنفيذ/مكتملة/ملغاة)
 *   - approvalState : حالة قرار الاعتماد (لا يلزم/بانتظار/معتمد/معاد)
 *
 * وحالة "متأخرة" ليست حالة مخزّنة، بل تُشتق من الموعد النهائي وحالة الإنجاز.
 * هذا يمنع فقدان معلومة التأخير عند الاعتماد، ويمنع تعارض "معتمدة ومتأخرة".
 */

import type { ApprovalState, CompletionRule, Task, TaskStatus } from './types';
import type { Instant } from './ids';

export type ActorKind = 'creator' | 'assignee' | 'approver' | 'supervisor' | 'manager' | 'system';

export interface TransitionRule {
  from: TaskStatus;
  to: TaskStatus;
  /** الصلاحية المطلوبة في طبقة الخدمة (لا تُطبَّق هنا، تُستخدم للتوثيق والتحقق) */
  permission: string | null;
  /** من يُسمح له */
  actors: ActorKind[];
  label: string;
}

const ALL_STATUSES: TaskStatus[] = ['scheduled', 'pending', 'in_progress', 'completed', 'cancelled'];

export const STATUS_LABELS: Record<TaskStatus, string> = {
  scheduled: 'مجدولة',
  pending: 'لم تبدأ',
  in_progress: 'قيد التنفيذ',
  completed: 'مكتملة',
  cancelled: 'ملغاة',
};

export const APPROVAL_LABELS: Record<ApprovalState, string> = {
  not_required: 'لا تتطلب اعتمادًا',
  pending: 'بانتظار الاعتماد',
  approved: 'معتمدة',
  returned: 'معادة للتصحيح',
};

export const TRANSITIONS: readonly TransitionRule[] = [
  {
    from: 'scheduled',
    to: 'pending',
    permission: null,
    actors: ['system'],
    label: 'حلول وقت البدء',
  },
  {
    from: 'scheduled',
    to: 'cancelled',
    permission: 'task.cancel',
    actors: ['creator', 'manager', 'supervisor'],
    label: 'إلغاء موعد لم يبدأ',
  },
  {
    from: 'scheduled',
    to: 'in_progress',
    permission: 'task.complete',
    actors: ['assignee', 'creator'],
    label: 'بدء التنفيذ مبكرًا',
  },
  {
    // إنجاز مباشر لمهمة مجدولة دون خطوة بدء صريحة (مسؤول واحد)
    from: 'scheduled',
    to: 'completed',
    permission: 'task.complete',
    actors: ['assignee', 'creator'],
    label: 'إنجاز مباشر',
  },
  {
    from: 'pending',
    to: 'in_progress',
    permission: 'task.complete',
    actors: ['assignee', 'creator'],
    label: 'بدء التنفيذ',
  },
  {
    // إنجاز مباشر لمهمة جاهزة دون خطوة بدء صريحة (مسؤول واحد)
    from: 'pending',
    to: 'completed',
    permission: 'task.complete',
    actors: ['assignee', 'creator'],
    label: 'إنجاز مباشر',
  },
  {
    from: 'pending',
    to: 'cancelled',
    permission: 'task.cancel',
    actors: ['creator', 'manager', 'supervisor'],
    label: 'إلغاء',
  },
  {
    from: 'in_progress',
    to: 'completed',
    permission: 'task.complete',
    actors: ['assignee', 'creator'],
    label: 'تسجيل الإنجاز',
  },
  {
    from: 'in_progress',
    to: 'cancelled',
    permission: 'task.cancel',
    actors: ['creator', 'manager', 'supervisor'],
    label: 'إلغاء',
  },
  {
    from: 'completed',
    to: 'in_progress',
    permission: 'task.reopen',
    actors: ['approver', 'manager', 'supervisor'],
    label: 'إعادة فتح',
  },
  {
    from: 'in_progress',
    to: 'in_progress',
    permission: 'task.return',
    actors: ['approver', 'manager'],
    label: 'إرجاع للتصحيح (يبقى قيد التنفيذ)',
  },
];

export interface TransitionCheck {
  allowed: boolean;
  reason: string;
}

export function canTransition(from: TaskStatus, to: TaskStatus): TransitionCheck {
  if (!ALL_STATUSES.includes(from) || !ALL_STATUSES.includes(to)) {
    return { allowed: false, reason: 'حالة غير معروفة' };
  }
  if (from === to) {
    return TRANSITIONS.some((t) => t.from === from && t.to === to)
      ? { allowed: true, reason: 'انتقال مسموح (إرجاع للتصحيح)' }
      : { allowed: false, reason: 'لا يوجد تغيير في الحالة' };
  }
  const rule = TRANSITIONS.find((t) => t.from === from && t.to === to);
  if (!rule) return { allowed: false, reason: `انتقال غير مسموح من "${STATUS_LABELS[from]}" إلى "${STATUS_LABELS[to]}"` };
  return { allowed: true, reason: rule.label };
}

export function allowedTransitions(from: TaskStatus): TransitionRule[] {
  return TRANSITIONS.filter((t) => t.from === from);
}

/* =============================================================================
 * قواعد الإنجاز الجماعي
 * ========================================================================== */

export interface AssignmentCompletionState {
  membershipId: string;
  state: 'pending' | 'completed';
  completedAt: Instant | null;
}

export interface CompletionEvaluation {
  /** هل يجب اعتبار المهمة كاملة كوحدة واحدة؟ */
  taskComplete: boolean;
  /** هل يجب أن ينتظر الإنجاز اعتمادًا؟ */
  needsApproval: boolean;
  reason: string;
  remainingCount: number;
}

/**
 * تقييم الإنجاز وفق قاعدة الإنجاز المحددة على المهمة.
 * لا تُنسب المهمة تلقائيًا إلى بقية المسؤولين عند إكمال أحدهم.
 */
export function evaluateCompletion(input: {
  rule: CompletionRule;
  assignees: AssignmentCompletionState[];
  requiresApproval: boolean;
}): CompletionEvaluation {
  const { rule, assignees, requiresApproval } = input;
  const total = assignees.length;
  const done = assignees.filter((a) => a.state === 'completed').length;
  const remaining = total - done;

  if (total === 0) {
    return {
      taskComplete: false,
      needsApproval: false,
      reason: 'لا يوجد مسؤولون عن المهمة',
      remainingCount: 0,
    };
  }

  const anyDone = done > 0;
  const allDone = remaining === 0;

  switch (rule) {
    case 'each':
      return {
        taskComplete: allDone,
        needsApproval: allDone && requiresApproval,
        reason: allDone ? 'أكمل جميع المسؤولين' : 'بانتظار إنجاز بقية المسؤولين',
        remainingCount: remaining,
      };
    case 'each_with_approval':
      // قاعدة صريحة: الاعتماد إلزامي لهذا النوع بمجرد اكتمال جميع المسؤولين.
      return {
        taskComplete: allDone,
        needsApproval: allDone,
        reason: allDone ? 'أكمل جميع المسؤولين وتتطلب اعتمادًا إلزاميًا' : 'بانتظار إنجاز بقية المسؤولين',
        remainingCount: remaining,
      };
    case 'any':
      return {
        taskComplete: anyDone,
        needsApproval: false,
        reason: anyDone ? 'أكمل أحد المسؤولين (يكفي واحد)' : 'لا أحد أكمل بعد',
        remainingCount: remaining,
      };
    case 'any_with_approval':
      return {
        taskComplete: anyDone,
        needsApproval: anyDone && requiresApproval,
        reason: anyDone ? 'أكمل أحد المسؤولين ويجب اعتماد الإنجاز' : 'لا أحد أكمل بعد',
        remainingCount: remaining,
      };
    default:
      return {
        taskComplete: false,
        needsApproval: false,
        reason: 'قاعدة إنجاز غير معروفة',
        remainingCount: remaining,
      };
  }
}

/* =============================================================================
 * التأخير وحالة العرض
 * ========================================================================== */

export type DisplayState =
  | TaskStatus
  | 'awaiting_approval'
  | 'approved'
  | 'returned'
  | 'overdue';

export interface DisplayStateInput {
  status: TaskStatus;
  approvalState: ApprovalState;
  dueAt: Instant | null;
  now: Date;
}

/**
 * حالة العرض المشتقة. التأخير لا يُخزَّن، لذا تبقى المهمة المتأخرة ظاهرة
 * كمتأخرة حتى لو حلّ موعدها التالي.
 */
export function displayState(input: DisplayStateInput): DisplayState {
  const { status, approvalState, dueAt, now } = input;

  if (status === 'cancelled') return 'cancelled';
  if (status === 'completed') {
    if (approvalState === 'pending') return 'awaiting_approval';
    if (approvalState === 'approved') return 'approved';
    if (approvalState === 'returned') return 'returned';
    return 'completed';
  }
  if (approvalState === 'returned') return 'returned';
  if (dueAt && new Date(dueAt).getTime() < now.getTime()) return 'overdue';
  return status;
}

export const DISPLAY_STATE_LABELS: Record<DisplayState, string> = {
  scheduled: 'مجدولة',
  pending: 'لم تبدأ',
  in_progress: 'قيد التنفيذ',
  completed: 'مكتملة',
  cancelled: 'ملغاة',
  awaiting_approval: 'بانتظار الاعتماد',
  approved: 'معتمدة',
  returned: 'معادة للتصحيح',
  overdue: 'متأخرة',
};

export function isOverdue(
  task: Pick<Task, 'status' | 'dueAt' | 'approvalState'>,
  now: Date = new Date(),
): boolean {
  if (task.status === 'completed' || task.status === 'cancelled') return false;
  if (!task.dueAt) return false;
  return new Date(task.dueAt).getTime() < now.getTime();
}

/** المهام التي تحتاج إجراءً من المستخدم الحالي */
export function needsAction(
  task: Pick<Task, 'status' | 'approvalState' | 'dueAt'>,
  role: ActorKind,
  now: Date = new Date(),
): boolean {
  if (task.status === 'cancelled') return false;
  if (role === 'approver') return task.approvalState === 'pending';
  if (task.status === 'completed') return false;
  if (task.approvalState === 'returned') return true;
  return isOverdue(task, now) || task.status !== 'scheduled' || role === 'assignee';
}

/** تطبيق إجراء الإنجاز: يعيد الحقول المحدَّثة فقط دون لمس بقية السجل. */
export function completionPatch(input: {
  requiresApproval: boolean;
  completedAt: Instant;
}): { status: TaskStatus; approvalState: ApprovalState; completedAt: Instant } {
  return {
    status: 'completed',
    approvalState: input.requiresApproval ? 'pending' : 'not_required',
    completedAt: input.completedAt,
  };
}

/** تطبيق قرار الاعتماد. */
export function approvalPatch(input: {
  decision: 'approve' | 'return';
  at: Instant;
  reason?: string;
}): {
  approvalState: ApprovalState;
  approvedAt: Instant | null;
  returnedAt: Instant | null;
  returnedReason: string | null;
  status: TaskStatus;
} {
  if (input.decision === 'approve') {
    return {
      approvalState: 'approved',
      approvedAt: input.at,
      returnedAt: null,
      returnedReason: null,
      status: 'completed',
    };
  }
  return {
    approvalState: 'returned',
    approvedAt: null,
    returnedAt: input.at,
    returnedReason: input.reason ?? null,
    status: 'in_progress',
  };
}

/** هل يمكن إعادة فتح مهمة مكتملة؟ */
export function canReopen(task: Pick<Task, 'status' | 'approvalState'>): TransitionCheck {
  if (task.status === 'completed' && task.approvalState !== 'pending') {
    return { allowed: true, reason: 'يمكن إعادة فتح المهمة' };
  }
  if (task.status === 'completed' && task.approvalState === 'pending') {
    return { allowed: false, reason: 'المهمة بانتظار قرار الاعتماد، استخدم قرار الاعتماد بدلًا من إعادة الفتح' };
  }
  return { allowed: false, reason: 'المهمة ليست مكتملة' };
}
